import React, { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { Typography, Checkbox } from '@mui/material';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodePlaceholderTable — Table with checkboxes for selecting/mapping parsed placeholders.
 * Used by: API Req/Res, Webhook, Trigger from Zapier, Parse JSON.
 */

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

const Table = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: NODE_UI.borderRadius,
  overflow: 'hidden',
}));

const HeaderRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '6px 8px',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fafafa',
  borderBottom: `1px solid ${theme.palette.divider}`,
  gap: 8,
}));

const HeaderCell = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  userSelect: 'none',
}));

const Row = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '4px 8px',
  gap: 8,
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': { borderBottom: 'none' },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const Cell = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontFamily: 'monospace',
}));

const TypeCell = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  color: theme.palette.text.secondary,
  minWidth: 50,
  flexShrink: 0,
}));

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  padding: '2px',
  color: theme.palette.text.secondary,
  '&.Mui-checked': { color: theme.palette.text.primary },
}));

const EmptyState = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  color: theme.palette.text.secondary,
  textAlign: 'center',
  padding: '16px 8px',
}));

const NodePlaceholderTable = React.memo(function NodePlaceholderTable({
  label,
  placeholders = [],
  selectedKeys = [],
  onSelectionChange,
  showCheckboxes = true,
  showType = true,
  emptyMessage = 'No placeholders found',
  sx,
}) {
  const handleToggle = useCallback((key) => {
    const isSelected = selectedKeys.includes(key);
    const next = isSelected
      ? selectedKeys.filter((k) => k !== key)
      : [...selectedKeys, key];
    onSelectionChange?.(next);
  }, [selectedKeys, onSelectionChange]);

  const handleToggleAll = useCallback(() => {
    if (selectedKeys.length === placeholders.length) {
      onSelectionChange?.([]);
    } else {
      onSelectionChange?.(placeholders.map((p) => p.key));
    }
  }, [selectedKeys, placeholders, onSelectionChange]);

  return (
    <div onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation}>
      {label && <Label>{label}</Label>}
      <Table sx={sx}>
        <HeaderRow>
          {showCheckboxes && (
            <StyledCheckbox
              size="small"
              checked={placeholders.length > 0 && selectedKeys.length === placeholders.length}
              indeterminate={selectedKeys.length > 0 && selectedKeys.length < placeholders.length}
              onChange={handleToggleAll}
            />
          )}
          <HeaderCell sx={{ flex: 1 }}>Placeholder</HeaderCell>
          {showType && <HeaderCell sx={{ minWidth: 50 }}>Type</HeaderCell>}
        </HeaderRow>

        {placeholders.length === 0 ? (
          <EmptyState>{emptyMessage}</EmptyState>
        ) : (
          placeholders.map((ph) => (
            <Row key={ph.key}>
              {showCheckboxes && (
                <StyledCheckbox
                  size="small"
                  checked={selectedKeys.includes(ph.key)}
                  onChange={() => handleToggle(ph.key)}
                />
              )}
              <Cell sx={{ flex: 1 }}>${'{'}${ph.key}{'}'}</Cell>
              {showType && <TypeCell>{ph.type || 'text'}</TypeCell>}
            </Row>
          ))
        )}
      </Table>
    </div>
  );
});

export default NodePlaceholderTable;
